import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Space,
  Switch,
  Tag,
  Typography
} from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  applicationsApplicationsScimPartialUpdateMutation,
  applicationsApplicationsScimRetrieveOptions,
  applicationsApplicationsScimRetrieveQueryKey,
  applicationsApplicationsScimRotateInboundTokenCreateMutation,
  type ScimSyncStatusEnum
} from 'src/api/main'
import { client as mainApiClient } from 'src/api/main/client.gen'
import { useShowApiErrors } from 'src/utils'

import { THIRD_PARTY_CUSTOM_APP_TYPE } from '../constants/constants'

type Props = {
  applicationId: number
  applicationType: string
}

type OutboundFormValues = {
  outbound_base_url?: string
  outbound_bearer_token?: string
}

const statusToColor: Record<ScimSyncStatusEnum, string> = {
  failed: 'error',
  idle: 'default',
  pending: 'processing',
  success: 'success'
}

export const AppScimPanel = ({ applicationId, applicationType }: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const showApiErrors = useShowApiErrors()
  const [outboundForm] = Form.useForm<OutboundFormValues>()
  const [visibleInboundToken, setVisibleInboundToken] = useState<string | null>(
    null
  )

  const isThirdParty = applicationType === THIRD_PARTY_CUSTOM_APP_TYPE
  const baseURL = String(mainApiClient.getConfig().baseURL ?? '').replace(
    /\/$/,
    ''
  )

  const { data: scimConfig, isLoading } = useQuery({
    ...applicationsApplicationsScimRetrieveOptions({
      path: { id: applicationId }
    }),
    enabled: !!applicationId && isThirdParty
  })

  const isScimEnabled =
    scimConfig?.inbound_enabled || scimConfig?.outbound_enabled || false

  // Local override for the outbound toggle. `null` = follow server state.
  const [outboundOverride, setOutboundOverride] = useState<boolean | null>(null)

  const outboundUIEnabled =
    outboundOverride ?? (scimConfig?.outbound_enabled || false)

  useEffect(() => {
    outboundForm.setFieldsValue({
      outbound_base_url: scimConfig?.outbound_base_url ?? '',
      outbound_bearer_token: ''
    })
  }, [outboundForm, scimConfig])

  const referenceItems = useMemo(() => {
    if (!baseURL || !applicationId) return []
    const scimBase = `${baseURL}/scim/v2`

    return [
      { key: 'base', label: 'Base URL', value: scimBase },
      { key: 'users', label: 'Users endpoint', value: `${scimBase}/Users` },
      { key: 'groups', label: 'Groups endpoint', value: `${scimBase}/Groups` },
      {
        key: 'auth',
        label: 'Authorization header',
        value: 'Authorization: Bearer <inbound-scim-token>'
      }
    ]
  }, [applicationId, baseURL])

  const invalidateScimConfig = () =>
    queryClient.invalidateQueries({
      queryKey: applicationsApplicationsScimRetrieveQueryKey({
        path: { id: applicationId }
      })
    })

  const { mutate: saveScimConfig, isPending: isSaving } = useMutation({
    ...applicationsApplicationsScimPartialUpdateMutation(),
    onSuccess: async () => {
      setOutboundOverride(null)
      await invalidateScimConfig()
      message.success(t('scim.settings_updated', 'SCIM settings updated'))
    },
    onError: (error) => showApiErrors(error, outboundForm)
  })

  const { mutate: rotateInboundToken, isPending: isRotatingToken } =
    useMutation({
      ...applicationsApplicationsScimRotateInboundTokenCreateMutation(),
      onSuccess: async (payload) => {
        const token = (payload as { inbound_token?: string }).inbound_token
        if (token) setVisibleInboundToken(token)
        await invalidateScimConfig()
      },
      onError: (error) => showApiErrors(error)
    })

  if (!isThirdParty) {
    return (
      <Alert
        type="info"
        showIcon
        title={t(
          'workspace.apps.scim.nonThirdPartyHint',
          'SCIM settings are available only for third-party custom applications.'
        )}
      />
    )
  }

  const isPending = isSaving || isRotatingToken

  const handleToggle = (checked: boolean) => {
    if (checked) {
      // Enable: generate token + enable inbound
      rotateInboundToken(
        { body: {} as never, path: { id: applicationId } },
        {
          onSuccess: () => {
            saveScimConfig({
              body: { inbound_enabled: true },
              path: { id: applicationId }
            })
          }
        }
      )
    } else {
      // Disable: turn off both inbound and outbound
      setVisibleInboundToken(null)
      saveScimConfig({
        body: { inbound_enabled: false, outbound_enabled: false },
        path: { id: applicationId }
      })
    }
  }

  const handleOutboundToggle = (next: boolean) => {
    setOutboundOverride(next)
    if (!next) {
      saveScimConfig({
        body: { outbound_enabled: false },
        path: { id: applicationId }
      })
    }
  }

  return (
    <Flex vertical gap={14}>
      {visibleInboundToken && (
        <Alert
          type="warning"
          showIcon
          closable={{ onClose: () => setVisibleInboundToken(null) }}
          title={t('scim.new_inbound_token', 'New inbound token')}
          description={
            <Space vertical size={8}>
              <Typography.Text>
                {t(
                  'scim.store_token_securely',
                  'Store this token securely. It will not be shown again.'
                )}
              </Typography.Text>
              <Typography.Paragraph copyable style={{ marginBottom: 0 }}>
                {visibleInboundToken}
              </Typography.Paragraph>
            </Space>
          }
        />
      )}

      <Card
        size="small"
        variant="outlined"
        title={t('scim.configuration', 'SCIM configuration')}
        loading={isLoading}
        extra={
          isScimEnabled ? (
            <Button
              size="small"
              onClick={() =>
                rotateInboundToken({
                  body: {} as never,
                  path: { id: applicationId }
                })
              }
              loading={isRotatingToken}
            >
              {t('scim.rotate_token', 'Rotate token')}
            </Button>
          ) : null
        }
      >
        <Flex vertical gap={16}>
          <Space align="center" size={12}>
            <Switch
              checked={isScimEnabled}
              onChange={handleToggle}
              loading={isPending}
            />
            <Typography.Text>
              {t('scim.enable_scim', 'Enable SCIM')}
            </Typography.Text>
          </Space>

          {isScimEnabled && (
            <>
              {scimConfig?.has_inbound_token && (
                <Space vertical size={4}>
                  <Typography.Text>
                    {t('scim.token_hint', 'Token hint')}:{' '}
                    <Typography.Text strong>
                      {scimConfig.inbound_token_hint ?? 'n/a'}
                    </Typography.Text>
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    {t('scim.rotated_at', 'Rotated at')}:{' '}
                    {scimConfig.inbound_token_rotated_at ?? 'n/a'}
                  </Typography.Text>
                </Space>
              )}

              <Divider />

              <Typography.Text strong>
                {t('scim.outbound', 'Outbound SCIM')}
              </Typography.Text>

              <Space align="center" size={12}>
                <Switch
                  checked={outboundUIEnabled}
                  onChange={handleOutboundToggle}
                  loading={isSaving}
                />
                <Typography.Text>
                  {t('scim.enable_outbound', 'Enable outbound')}
                </Typography.Text>
              </Space>

              {outboundUIEnabled && (
                <Form
                  form={outboundForm}
                  layout="vertical"
                  requiredMark={false}
                  onFinish={async (values) => {
                    const trimmedUrl = values.outbound_base_url?.trim() || null
                    const outboundBearerToken =
                      values.outbound_bearer_token?.trim() || undefined

                    if (
                      !scimConfig?.has_outbound_token &&
                      !outboundBearerToken
                    ) {
                      outboundForm.setFields([
                        {
                          name: 'outbound_bearer_token',
                          errors: [
                            t(
                              'scim.token_required',
                              'Token is required to enable outbound SCIM.'
                            )
                          ]
                        }
                      ])
                      return
                    }

                    saveScimConfig({
                      body: {
                        outbound_enabled: true,
                        outbound_base_url: trimmedUrl,
                        ...(outboundBearerToken
                          ? { outbound_bearer_token: outboundBearerToken }
                          : {})
                      },
                      path: { id: applicationId }
                    })
                  }}
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={t('scim.remote_base_url', 'Remote base URL')}
                        name="outbound_base_url"
                        rules={[
                          { required: true, message: 'Required' },
                          {
                            validator: (_, value: string | undefined) => {
                              const trimmed = value?.trim()
                              if (!trimmed) return Promise.resolve()
                              if (/^https?:\/\/\S+/i.test(trimmed)) {
                                return Promise.resolve()
                              }
                              return Promise.reject(
                                new Error(
                                  'URL must start with http:// or https://'
                                )
                              )
                            }
                          }
                        ]}
                      >
                        <Input placeholder="https://example.com/scim/v2" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={t(
                          'scim.remote_bearer_token',
                          'Remote bearer token'
                        )}
                        name="outbound_bearer_token"
                        extra={
                          scimConfig?.has_outbound_token
                            ? t(
                                'scim.keep_stored_token',
                                'Leave empty to keep stored token'
                              )
                            : undefined
                        }
                      >
                        <Input.Password
                          placeholder={
                            scimConfig?.has_outbound_token
                              ? t('scim.stored_securely', 'Stored securely')
                              : t(
                                  'scim.paste_remote_token',
                                  'Paste remote token'
                                )
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Flex justify="space-between" align="center" gap={16}>
                    <Space wrap>
                      <Tag
                        color={
                          statusToColor[scimConfig?.last_sync_status ?? 'idle']
                        }
                      >
                        {t('scim.sync', 'Sync')}:{' '}
                        {scimConfig?.last_sync_status ?? 'idle'}
                      </Tag>
                      <Tag
                        color={
                          statusToColor[
                            scimConfig?.last_backfill_status ?? 'idle'
                          ]
                        }
                      >
                        {t('scim.backfill', 'Backfill')}:{' '}
                        {scimConfig?.last_backfill_status ?? 'idle'}
                      </Tag>
                    </Space>

                    <Button
                      size="small"
                      type="primary"
                      htmlType="submit"
                      loading={isSaving}
                    >
                      {scimConfig?.outbound_enabled
                        ? t('button.save', 'Save')
                        : t('scim.save_and_enable', 'Save & enable')}
                    </Button>
                  </Flex>

                  {scimConfig?.outbound_enabled &&
                    (scimConfig?.last_sync_error ||
                      scimConfig?.last_backfill_error) && (
                      <Alert
                        style={{ marginTop: 16 }}
                        type="error"
                        showIcon
                        title={t('scim.latest_error', 'Latest error')}
                        description={
                          scimConfig?.last_backfill_error ??
                          scimConfig?.last_sync_error
                        }
                      />
                    )}
                </Form>
              )}
            </>
          )}
        </Flex>
      </Card>

      {isScimEnabled && (
        <Card
          size="small"
          variant="outlined"
          title={t('scim.inbound_reference', 'Inbound reference')}
        >
          <Flex vertical gap={12}>
            {referenceItems.map((item) => (
              <div key={item.key}>
                <Typography.Text strong>{item.label}</Typography.Text>
                <Typography.Paragraph copyable style={{ marginBottom: 0 }}>
                  {item.value}
                </Typography.Paragraph>
              </div>
            ))}
          </Flex>
        </Card>
      )}
    </Flex>
  )
}
